/**
 * Commercial facts for the post-quiz paywall funnel — one module, no second copy.
 *
 * Every price, period and guarantee shown on the funnel is read from here. The
 * handoff's rule is "own numbers only": a screen may not print a figure this
 * file does not carry, and this file may not carry a figure the billing side
 * cannot honour.
 *
 * Sources:
 *   - price / trial   → `PLANS[0]` in `components/PricingPage.tsx`
 *   - refund window   → `components/RefundPage.tsx`
 *   - promo code      → the Polar discount, verified live (see `api/polar-discount.js`)
 *   - billing portal  → `lib/polar.ts`
 */

/** Monthly subscription that starts when the free trial ends. */
export const PRICE_MONTH = 19.99;

/** Weekly equivalent, as advertised on the pricing page. */
export const PRICE_WEEK = 4.99;

/** Free trial length, in days. Nothing is charged during it. */
export const TRIAL_DAYS = 7;

/** Money-back window after the first charge, in days. */
export const REFUND_DAYS = 30;

/**
 * Promo code granted on the scratch card.
 *
 * The percentage here is only a fallback for rendering: the funnel asks
 * `/api/polar-discount` for the live value first, so the number on screen is
 * the number Polar will actually apply. If no code is configured the scratch
 * step is skipped entirely rather than inventing a discount.
 */
export const PROMO_CODE = (process.env.REACT_APP_PROMO_CODE ?? 'dys1m324').trim();

/** Fallback percentage, used only when the live lookup fails. */
export const PROMO_PERCENT = Number(process.env.REACT_APP_PROMO_PERCENT ?? 30);

/**
 * How long the granted promo stays valid, in minutes.
 *
 * The countdown runs from `promoGrantedAt` and never restarts — see
 * `usePaywallSession`. A timer that resets on every visit is a lie the user
 * can catch by reloading.
 */
export const PROMO_MINUTES = 10;

/** Money formatting: `$19.99`, and `$0.67` for the per-day figures. */
export const money = (v: number): string => `$${v.toFixed(2)}`;

/** Per-day cost of a monthly price, over a 30-day month. */
export const perDay = (monthly: number): string => money(monthly / 30);

/** The discounted first month, from a percentage off the monthly price. */
export const discounted = (percent: number): number => Math.round(PRICE_MONTH * (1 - percent / 100) * 100) / 100;

export interface PromoState {
    /** A live-verified Polar discount exists for `PROMO_CODE`. */
    valid: boolean;
    code: string;
    percent: number;
    /** First-month price after the discount. */
    price: number;
}

/**
 * Ask the backend whether the configured code is a real, live Polar discount,
 * and what it is worth. Returns `valid: false` on any failure — the caller then
 * skips the discount step instead of promising money it cannot honour.
 */
export async function verifyPromo(): Promise<PromoState> {
    const miss: PromoState = { valid: false, code: PROMO_CODE, percent: 0, price: PRICE_MONTH };

    if (!PROMO_CODE) return miss;

    try {
        const res = await fetch(`/api/polar-discount?code=${encodeURIComponent(PROMO_CODE)}`, {
            cache: 'no-store',
        });

        if (!res.ok) return miss;
        const data = (await res.json()) as { valid?: boolean; percent?: number };

        if (!data.valid || !data.percent) return miss;

        return {
            valid: true,
            code: PROMO_CODE,
            percent: data.percent,
            price: discounted(data.percent),
        };
    } catch {
        // The endpoint is unreachable (offline, cold start). The code itself is
        // configured and checkout resolves it server-side anyway, so fall back
        // to the configured percentage rather than dropping a real offer.
        return PROMO_PERCENT > 0
            ? { valid: true, code: PROMO_CODE, percent: PROMO_PERCENT, price: discounted(PROMO_PERCENT) }
            : miss;
    }
}
